import { useCallback, useEffect, useMemo, useState } from 'react'
import { format, subDays } from 'date-fns'
import {
  Activity,
  BarChart3,
  MousePointerClick,
  MonitorSmartphone,
  Target,
  TrendingUp,
  UserCheck,
  Users,
} from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import LoadErrorState from '../components/common/LoadErrorState'
import { SERVER_ERROR_MESSAGE, SERVER_TIMEOUT_MESSAGE, withServerTimeout } from '../components/common/loadErrorUtils'
import { useColors } from '../hooks/useColors'
import { marketingApi } from '../services/marketingApi'
import type { FunnelStep, MarketingOverview, MarketingVisitor } from '../services/marketingApi'

type TabId = 'overview' | 'funnel' | 'visitors'
type DateRange = '7d' | '30d' | 'all'

const CARD_SHADOW = '0px 2px 8px rgba(0,0,0,0.04)'

const formatNumber = (value?: number | null) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0))

const getDateParams = (range: DateRange) => {
  if (range === 'all') return {}
  const days = range === '7d' ? 7 : 30
  return { startDate: subDays(new Date(), days).toISOString() }
}

function SkeletonCard({ colors, height = 156 }: { colors: ReturnType<typeof useColors>; height?: number }) {
  return (
    <div
      className="rapidry-pulse"
      style={{
        height,
        borderRadius: '16px',
        background: colors.surface,
      }}
    />
  )
}

function StatCard({
  colors,
  icon: Icon,
  value,
  label,
  badge,
  badgeBg,
  badgeColor,
}: {
  colors: ReturnType<typeof useColors>
  icon: typeof Users
  value: string
  label: string
  badge?: string
  badgeBg?: string
  badgeColor?: string
}) {
  return (
    <article
      style={{
        flex: 1,
        minWidth: 0,
        background: colors.bgCard,
        borderRadius: '16px',
        padding: '20px 24px',
        boxShadow: CARD_SHADOW,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '999px',
            background: colors.surface,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Icon size={20} color={colors.textPrimary} strokeWidth={2} />
        </div>
        {badge ? (
          <span
            className="font-body"
            style={{
              background: badgeBg,
              color: badgeColor,
              borderRadius: '999px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            {badge}
          </span>
        ) : null}
      </div>
      <div className="font-display" style={{ fontSize: '28px', fontWeight: 700, lineHeight: 1, color: colors.textPrimary, marginBottom: '4px' }}>
        {value}
      </div>
      <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textSecondary }}>
        {label}
      </p>
    </article>
  )
}

function ChartTooltip({
  active,
  payload,
  label,
  colors,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ value?: unknown }>
  label?: string | number
  colors: ReturnType<typeof useColors>
}) {
  if (!active || !payload?.length) return null

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: '10px',
        boxShadow: '0px 12px 32px rgba(15,46,42,0.12)',
        padding: '10px 14px',
      }}
    >
      <div className="font-body" style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '4px' }}>
        {label}
      </div>
      <div className="font-body" style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
        {formatNumber(Number(payload[0]?.value || 0))} visitors
      </div>
    </div>
  )
}

export default function MarketingPage() {
  const colors = useColors()
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [dateRange, setDateRange] = useState<DateRange>('30d')

  const [overview, setOverview] = useState<MarketingOverview | null>(null)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [overviewError, setOverviewError] = useState('')

  const [funnel, setFunnel] = useState<FunnelStep[]>([])
  const [funnelLoading, setFunnelLoading] = useState(true)
  const [funnelError, setFunnelError] = useState('')

  const [visitors, setVisitors] = useState<MarketingVisitor[]>([])
  const [visitorsLoading, setVisitorsLoading] = useState(true)
  const [visitorsError, setVisitorsError] = useState('')

  const dateParams = useMemo(() => getDateParams(dateRange), [dateRange])

  const getLoadMessage = (loadError: unknown) =>
    loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true)
    setOverviewError('')
    try {
      const response = await withServerTimeout(marketingApi.getOverview(dateParams))
      setOverview(response.data.data)
    } catch (loadError) {
      setOverviewError(getLoadMessage(loadError))
    } finally {
      setOverviewLoading(false)
    }
  }, [dateParams])

  const loadFunnel = useCallback(async () => {
    setFunnelLoading(true)
    setFunnelError('')
    try {
      const response = await withServerTimeout(marketingApi.getFunnel(dateParams))
      setFunnel(response.data.data || [])
    } catch (loadError) {
      setFunnelError(getLoadMessage(loadError))
    } finally {
      setFunnelLoading(false)
    }
  }, [dateParams])

  const loadVisitors = useCallback(async () => {
    setVisitorsLoading(true)
    setVisitorsError('')
    try {
      const response = await withServerTimeout(marketingApi.getVisitors({ limit: 50 }))
      setVisitors(response.data.data || [])
    } catch (loadError) {
      setVisitorsError(getLoadMessage(loadError))
    } finally {
      setVisitorsLoading(false)
    }
  }, [])

  const loadAll = useCallback(() => {
    void loadOverview()
    void loadFunnel()
    void loadVisitors()
  }, [loadFunnel, loadOverview, loadVisitors])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const tabs: Array<{ id: TabId; name: string; icon: typeof Activity }> = [
    { id: 'overview', name: 'Overview', icon: Activity },
    { id: 'funnel', name: 'Conversion Funnel', icon: Target },
    { id: 'visitors', name: 'Recent Visitors', icon: Users },
  ]

  const funnelColors = ['#0F766E', '#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4']

  const hasAnyError = overviewError && funnelError && visitorsError

  if (hasAnyError) {
    return <LoadErrorState message={overviewError || funnelError || visitorsError} onRetry={loadAll} />
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div>
          <h1 className="font-display" style={{ margin: 0, fontSize: '26px', color: colors.textPrimary }}>
            Marketing & Analytics
          </h1>
          <p className="font-body" style={{ margin: '6px 0 0', color: colors.textMuted, fontSize: '14px' }}>
            Track website visitors, campaign performance, and conversion funnels.
          </p>
        </div>

        <div style={{ display: 'inline-flex', background: colors.surface, borderRadius: '12px', padding: '3px', gap: '3px' }}>
          {([
            { key: '7d', label: '7 Days' },
            { key: '30d', label: '30 Days' },
            { key: 'all', label: 'All Time' },
          ] as const).map((period) => {
            const isActive = dateRange === period.key
            return (
              <button
                key={period.key}
                type="button"
                className="font-body"
                onClick={() => setDateRange(period.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: isActive ? colors.textPrimary : 'transparent',
                  color: isActive ? colors.bgCard : colors.textSecondary,
                  fontSize: '12px',
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                }}
              >
                {period.label}
              </button>
            )
          })}
        </div>
      </div>

      <div
        style={{
          display: 'inline-flex',
          background: colors.surface,
          borderRadius: '12px',
          padding: '4px',
          gap: '4px',
          marginBottom: '24px',
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              className="font-body"
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '10px',
                border: 'none',
                background: isActive ? colors.bgCard : 'transparent',
                color: isActive ? colors.textPrimary : colors.textSecondary,
                fontSize: '13px',
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                boxShadow: isActive ? CARD_SHADOW : 'none',
              }}
            >
              <tab.icon size={16} strokeWidth={2} />
              {tab.name}
            </button>
          )
        })}
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gap: '24px' }}>
          {overviewLoading ? (
            <section style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <SkeletonCard colors={colors} />
              <SkeletonCard colors={colors} />
              <SkeletonCard colors={colors} />
              <SkeletonCard colors={colors} />
            </section>
          ) : (
            <section style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <StatCard
                colors={colors}
                icon={Users}
                value={formatNumber(overview?.totalVisitors)}
                label="Total Unique Visitors"
              />
              <StatCard
                colors={colors}
                icon={Activity}
                value={formatNumber(overview?.totalSessions)}
                label="Total Sessions"
                badge={`${formatNumber(overview?.returningVisitors)} returning`}
                badgeBg="#EFF6FF"
                badgeColor="#1D4ED8"
              />
              <StatCard
                colors={colors}
                icon={UserCheck}
                value={formatNumber(overview?.identifiedVisitors)}
                label="Identified Customers"
                badge={`${formatNumber(overview?.anonymousVisitors)} anonymous`}
                badgeBg="#F3F4F6"
                badgeColor="#374151"
              />
              <StatCard
                colors={colors}
                icon={TrendingUp}
                value={`${(overview?.conversionRate ?? 0).toFixed(1)}%`}
                label="Visitor → Book Now Rate"
                badge={`${formatNumber(overview?.bookNowClicks)} clicks`}
                badgeBg="#F0FDF4"
                badgeColor="#15803D"
              />
            </section>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            <section
              style={{
                background: colors.bgCard,
                borderRadius: '16px',
                padding: '24px',
                boxShadow: CARD_SHADOW,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <MousePointerClick size={18} color={colors.textPrimary} />
                <h2 className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                  Top Traffic Sources
                </h2>
              </div>

              {overviewLoading ? (
                <SkeletonCard colors={colors} height={180} />
              ) : overview?.topSources?.length ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {overview.topSources.map((source, index) => {
                    const maxCount = overview.topSources[0]?.count || 1
                    const widthPct = Math.max(8, (source.count / maxCount) * 100)
                    return (
                      <div key={`${source.source}-${index}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                            {source.source || '(direct)'}
                          </span>
                          <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                            {formatNumber(source.count)} visitors
                          </span>
                        </div>
                        <div style={{ height: '8px', borderRadius: '999px', background: colors.surface, overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${widthPct}%`,
                              height: '100%',
                              borderRadius: '999px',
                              background: colors.accent,
                            }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted, textAlign: 'center', padding: '24px 0' }}>
                  No attribution data yet. UTM parameters will appear here once campaigns drive traffic.
                </p>
              )}
            </section>

            <section
              style={{
                background: colors.bgCard,
                borderRadius: '16px',
                padding: '24px',
                boxShadow: CARD_SHADOW,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <BarChart3 size={18} color={colors.textPrimary} />
                <h2 className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                  Top Services Viewed
                </h2>
              </div>

              {overviewLoading ? (
                <SkeletonCard colors={colors} height={180} />
              ) : overview?.topServices?.length ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {overview.topServices.map((service, index) => {
                    const maxCount = overview.topServices![0]?.count || 1
                    const widthPct = Math.max(8, (service.count / maxCount) * 100)
                    return (
                      <div key={`${service.name}-${index}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                            {service.name}
                          </span>
                          <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                            {formatNumber(service.count)} views
                          </span>
                        </div>
                        <div style={{ height: '8px', borderRadius: '999px', background: colors.surface, overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${widthPct}%`,
                              height: '100%',
                              borderRadius: '999px',
                              background: '#0D9488',
                            }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted, textAlign: 'center', padding: '24px 0' }}>
                  No service views recorded yet.
                </p>
              )}
            </section>
          </div>
        </div>
      )}

      {activeTab === 'funnel' && (
        <section
          style={{
            background: colors.bgCard,
            borderRadius: '16px',
            padding: '24px',
            boxShadow: CARD_SHADOW,
            border: `1px solid ${colors.border}`,
          }}
        >
          <h2 className="font-display" style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>
            Website Conversion Funnel
          </h2>
          <p className="font-body" style={{ margin: '0 0 24px', fontSize: '13px', color: colors.textMuted }}>
            How visitors progress from landing on the site to clicking Book Now.
          </p>

          {funnelError ? (
            <LoadErrorState message={funnelError} onRetry={() => void loadFunnel()} margin="0" />
          ) : funnelLoading ? (
            <SkeletonCard colors={colors} height={380} />
          ) : (
            <div style={{ height: '380px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnel} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.border} />
                  <XAxis dataKey="step" tick={{ fill: colors.textSecondary, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: colors.textSecondary, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip colors={colors} />} cursor={{ fill: colors.surface }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={72}>
                    {funnel.map((entry, index) => (
                      <Cell key={entry.step} fill={funnelColors[index % funnelColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      )}

      {activeTab === 'visitors' && (
        <section
          style={{
            background: colors.bgCard,
            borderRadius: '16px',
            boxShadow: CARD_SHADOW,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.border}` }}>
            <h2 className="font-display" style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>
              Recent Visitors
            </h2>
            <p className="font-body" style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textMuted }}>
              Latest website sessions with attribution and identification status.
            </p>
          </div>

          {visitorsError ? (
            <LoadErrorState message={visitorsError} onRetry={() => void loadVisitors()} margin="0" />
          ) : visitorsLoading ? (
            <div style={{ padding: '24px' }}>
              <SkeletonCard colors={colors} height={280} />
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.surface }}>
                    {['Last Seen', 'Source', 'Device', 'Status'].map((heading) => (
                      <th
                        key={heading}
                        className="font-body"
                        style={{
                          padding: '12px 24px',
                          textAlign: 'left',
                          fontSize: '11px',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          color: colors.textMuted,
                        }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visitors.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="font-body" style={{ padding: '32px 24px', textAlign: 'center', color: colors.textMuted, fontSize: '13px' }}>
                        No visitors recorded yet. Tracking starts when someone visits rapidry.in.
                      </td>
                    </tr>
                  ) : (
                    visitors.map((visitor) => (
                      <tr key={visitor.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td className="font-body" style={{ padding: '14px 24px', fontSize: '13px', color: colors.textPrimary, whiteSpace: 'nowrap' }}>
                          {format(new Date(visitor.last_seen_at), 'MMM d, h:mm a')}
                        </td>
                        <td className="font-body" style={{ padding: '14px 24px', fontSize: '13px', color: colors.textSecondary }}>
                          {visitor.first_utm_source || '(direct)'}
                        </td>
                        <td className="font-body" style={{ padding: '14px 24px', fontSize: '13px', color: colors.textSecondary }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <MonitorSmartphone size={14} color={colors.textMuted} />
                            {visitor.device_category || 'Unknown'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 24px' }}>
                          <span
                            className="font-body"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 10px',
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: visitor.customer_id ? '#F0FDF4' : colors.surface,
                              color: visitor.customer_id ? '#15803D' : colors.textSecondary,
                              border: visitor.customer_id ? '1px solid #BBF7D0' : `1px solid ${colors.border}`,
                            }}
                          >
                            {visitor.customer_id ? 'Identified' : 'Anonymous'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
